import {create} from 'zustand';
import {devtools} from 'zustand/middleware';
import {TherapiesDataStoreProps, TherapyData} from './types';
import {emptySessionBuilder} from '../../components/AddTherapy/hooks/useFormManagement';

export const useTherapiesDataStore = create<TherapiesDataStoreProps>()(
	devtools(
		(set, get) => ({
			therapies: [],
			editingTherapy: null,
			lastSequentialProcedure: 1,
			modalEditDataAndCleanProcedures: false,
			incrementLastSequentialProcedure: () =>
				set(state => ({
					lastSequentialProcedure: state.lastSequentialProcedure + 1,
				})),
			resetLastSequentialProcedure: () =>
				set(() => ({
					lastSequentialProcedure: 1,
				})),
			openModalEditDataAndCleanProcedures: () =>
				set(() => ({modalEditDataAndCleanProcedures: true})),
			closeModalEditDataAndCleanProcedures: () =>
				set(() => ({modalEditDataAndCleanProcedures: false})),
			addTherapy: (therapy: TherapyData) =>
				set(
					state => ({
						therapies: [...state.therapies, therapy],
						editingTherapy: null,
					}),
					false,
					'addTherapy',
				),
			removeTherapy: (index: number) =>
				set(
					state => {
						if (index < 0 || index >= state.therapies.length) {
							return state;
						}

						return {
							therapies: state.therapies.filter((_, i) => i !== index),
						};
					},
					false,
					'removeTherapy',
				),
			resetTherapies: () =>
				set(
					() => ({
						therapies: [],
						editingTherapy: null,
						lastSequentialProcedure: 1,
						modalEditDataAndCleanProcedures: false,
					}),
					false,
					'resetTherapies',
				),
			moveTherapyToEdit: (index: number) => {
				const state = get();
				if (index < 0 || index >= state.therapies.length) {
					return null;
				}

				const therapyToEdit = state.therapies[index];

				const greaterSessionIdOfAllTherapies = state.therapies.reduce((maxId, therapy) => {
					const therapyMaxId = therapy.sessions?.reduce((therapyMax, session) => {
						const sessionIdNum = session.id ? Number(session.id) : 0;
						return sessionIdNum > therapyMax ? sessionIdNum : therapyMax;
					}, 0);
					return (therapyMaxId ?? 0) > maxId ? (therapyMaxId ?? 0) : maxId;
				}, 0);

				const editableTherapy = {
					...therapyToEdit,
					sessions: [
						...(therapyToEdit.sessions || []),
						emptySessionBuilder(greaterSessionIdOfAllTherapies + 1),
					],
				};

				const newTherapies = state.therapies.filter((_, i) => i !== index);

				set(
					{
						therapies: newTherapies,
						editingTherapy: {
							therapy: editableTherapy,
							originalIndex: index,
						},
					},
					false,
					'moveTherapyToEdit',
				);

				return editableTherapy;
			},
			restoreTherapyFromEdit: () =>
				set(
					state => {
						if (!state.editingTherapy) {
							return state;
						}

						const {therapy, originalIndex} = state.editingTherapy;

						const therapyWithoutEmptySession = {
							...therapy,
							sessions:
								therapy?.sessions?.filter(
									session =>
										session?.serviceDate &&
										session?.sessionValue &&
										session?.id,
								) || [],
						};

						const newTherapies = [...state.therapies];
						const insertIndex = Math.min(originalIndex, newTherapies.length);
						newTherapies.splice(insertIndex, 0, therapyWithoutEmptySession);

						return {
							therapies: newTherapies,
							editingTherapy: null,
						};
					},
					false,
					'restoreTherapyFromEdit',
				),
			updateTherapyFromEdit: (updatedTherapy: TherapyData) =>
				set(
					state => {
						if (!state.editingTherapy) {
							return state;
						}

						const {originalIndex} = state.editingTherapy;
						const newTherapies = [...state.therapies];

						const insertIndex = Math.min(originalIndex, newTherapies.length);
						newTherapies.splice(insertIndex, 0, updatedTherapy);

						return {
							therapies: newTherapies,
							editingTherapy: null,
						};
					},
					false,
					'updateTherapyFromEdit',
				),
		}),
		{
			name: 'therapies-data-store',
		},
	),
);
